import store from "@/store";
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { VIEWPOINT_API } from "@/api/ViewpointApi";
import { Viewpoint } from "@/api/model/Viewpoint";
import UserAuth from "@/store/auth/UserAuth";
import { ViewpointFactorResource } from "@/api/model/ViewpointFactorResource";
import { ViewpointChoiceFactor } from "@/api/model/ViewpointChoiceFactor";
import _ from "lodash";
import Vue from "vue";
import DecisionModule from "@/store/decision/Decision";
import Properties from "@/store/property/Properties";
import FavouritesModule from "@/store/Favourites";


config.rawError = true

const name = 'viewpoint';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

@Module({dynamic: true, store, name: name, namespaced: true})
export default class ViewpointsModule extends VuexModule {

    viewpoints: Viewpoint[] = [];

    selectedViewpointId: string = ''

    viewpointFactors: { [id: string]: ViewpointFactorResource } = {};

    get selectedViewpoint() {
        return this.viewpoints.find(vp => vp.viewpointId === this.selectedViewpointId)
    }

    @Mutation
    setViewpoints(viewpoints: Viewpoint[]) {
        this.viewpoints = viewpoints;
    }

    @Mutation
    setViewpoint(viewpoint: Viewpoint) {
        let index = this.viewpoints.findIndex(vp => vp.viewpointId === viewpoint.viewpointId);
        this.viewpoints.splice(index, 1, viewpoint)
    }

    @Mutation
    addViewpoint(viewpoint: Viewpoint) {
        this.viewpoints.splice(this.viewpoints.length, 0, viewpoint)
    }

    @Mutation
    removeViewpoint(viewpointId: string) {
        let index = this.viewpoints.findIndex(vp => vp.viewpointId === viewpointId);
        this.viewpoints.splice(index, 1)
    }

    @Mutation
    setViewpointFactors(factors: ViewpointFactorResource[]) {
        this.viewpointFactors = _.keyBy(factors, 'id')
    }

    @Mutation
    updateViewpointFactors(factors: ViewpointFactorResource[]) {
        factors.forEach(f => Vue.set(this.viewpointFactors, f.id, f))
    }


    @Mutation
    setSelectedViewpointId(viewpointId: string) {
        this.selectedViewpointId = viewpointId;
    }


    @Action
    async getViewpoints(decisionId: string) {
        let viewpoints = await VIEWPOINT_API.getDecisionViewpoints(decisionId);
        this.setViewpoints(viewpoints)
        let vp = viewpoints.find(vp => vp.ownerUserId === getModule(UserAuth).userId)
        if (vp == null) {
            let user = getModule(UserAuth).user
            let name = user ? user.first : "Viewpoint"
            let newViewpoint = await VIEWPOINT_API.createViewpoint(decisionId, name, "")
            this.addViewpoint(newViewpoint);
            this.selectViewpoint(newViewpoint.viewpointId)
        } else {
            this.selectViewpoint(vp.viewpointId)
        }
    }

    @Action
    async selectViewpoint(viewpointId: string) {
        if (this.viewpoints) {
            let viewpointFactorResources = await VIEWPOINT_API.getViewpointFactorResources(viewpointId)
            this.setViewpointFactors(viewpointFactorResources)
            this.setSelectedViewpointId(viewpointId);
            await getModule(Properties).getProperties()
            if (!getModule(UserAuth).isGuest) {
                await getModule(FavouritesModule).getFavourites()
            }
        }
    }

    @Action
    async createViewpoint(payload: { name: string, description: string }) {
        let decision = getModule(DecisionModule).decision
        if (!decision) return;
        let viewpoint = await VIEWPOINT_API.createViewpoint(decision.decisionId, payload.name, payload.description)
        this.addViewpoint(viewpoint);
        await this.selectViewpoint(viewpoint.viewpointId)
        return viewpoint
    }

    @Action
    async updateViewpoint(payload: { id: string, name: string, description: string }) {
        let decision = getModule(DecisionModule).decision
        let viewpoint = this.viewpoints.find(vp => vp.viewpointId === payload.id)
        if (!decision || !viewpoint) return;
        let viewpointCopy = _.cloneDeep(viewpoint);
        viewpointCopy.name = payload.name
        viewpointCopy.description = payload.description
        let updatedViewpoint = await VIEWPOINT_API.updateViewpoint(viewpointCopy)
        this.setViewpoint(updatedViewpoint)
        return updatedViewpoint;
    }

    @Action
    async deleteViewpoint(viewpointId: string) {
        if (this.selectedViewpointId === viewpointId) {
            let remainingViewpoints = this.viewpoints.filter(v => v.viewpointId !== viewpointId);
            if (remainingViewpoints.length) {
                await this.selectViewpoint(remainingViewpoints[0].viewpointId)
            }
        }
        let deletedViewpoint = await VIEWPOINT_API.deleteViewpoint(viewpointId)
        this.removeViewpoint(viewpointId)
        return deletedViewpoint
    }

    @Action
    async putViewpointFactors(factors: ViewpointFactorResource[]) {
        let updatedFactorResources = await VIEWPOINT_API.putViewpointFactorResources(this.selectedViewpointId, factors);
        this.updateViewpointFactors(updatedFactorResources)
        getModule(Properties).getProperties()
        if (!getModule(UserAuth).isGuest) {
            getModule(FavouritesModule).updateScores()
        }
        return updatedFactorResources;
    }

    @Action
    async clear() {
        this.setSelectedViewpointId('')
        this.setViewpointFactors([])
        this.setViewpoints([])

    }

}